import type { LatestWorkflowForEntityResponse } from '@/shared/hooks/api/workflows'
import {
  PRODUCT_LAW_KEYS,
  type ApplicabilityPackSummary,
  type ProductLawApplicabilityStatus,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { extractProductDiscoverVerdicts } from './productDiscoverWorkflow'

export type DiscoverQuestionBeadState =
  | 'pending'
  | 'running'
  | 'yes'
  | 'no'
  | 'gap'

export type DiscoverChamberQuestion = {
  questionKey: string
  label: string
  state: DiscoverQuestionBeadState
  rationale: string | null
  retrievalQuery: string | null
}

export type DiscoverChamberLaw = {
  law: ProductLawKey
  phase: 'idle' | 'running' | 'done'
  skipped: boolean
  verdict: ProductLawApplicabilityStatus | null
  questions: DiscoverChamberQuestion[]
}

export type DiscoverChamberState = {
  laws: DiscoverChamberLaw[]
  followUpMessageKey: string | null
  isComplete: boolean
  isFailed: boolean
  errorMessage: string | null
  answeredCount: number
  totalCount: number
  activeNarration: DiscoverChamberQuestion | null
  lastRetrievalQuery: string | null
}

const QUESTION_STEP_PATTERN = /^([a-z]+)_question_(q\d+)$/
const DISCOVER_CHAMBER_LAW_ORDER: ProductLawKey[] = [...PRODUCT_LAW_KEYS]

function isProductLawKey(value: string): value is ProductLawKey {
  return (PRODUCT_LAW_KEYS as readonly string[]).includes(value)
}

function parseQuestionStep(
  step: string | undefined
): { law: ProductLawKey; questionKey: string } | null {
  if (step == null) {
    return null
  }
  const match = step.match(QUESTION_STEP_PATTERN)
  if (match == null) {
    return null
  }
  const law = match[1]
  const questionKey = match[2]
  if (law == null || questionKey == null || !isProductLawKey(law)) {
    return null
  }
  return { law, questionKey }
}

function lawFromFirstPassStep(step: string | undefined): ProductLawKey | null {
  if (step == null || !step.endsWith('_first_pass')) {
    return null
  }
  const law = step.slice(0, -'_first_pass'.length)
  if (!isProductLawKey(law)) {
    return null
  }
  return law
}

function emptyLaws(
  packs: ApplicabilityPackSummary[] | undefined
): DiscoverChamberLaw[] {
  const packsByLaw = new Map(
    (packs ?? []).map((pack) => [pack.law, pack.questions])
  )
  return DISCOVER_CHAMBER_LAW_ORDER.map((law) => ({
    law,
    phase: 'idle' as const,
    skipped: false,
    verdict: null,
    questions: (packsByLaw.get(law) ?? []).map((question) => ({
      questionKey: question.questionKey,
      label: question.label,
      state: 'pending' as const,
      rationale: null,
      retrievalQuery: null,
    })),
  }))
}

function beadStateFromResult(
  result: Record<string, unknown> | undefined
): DiscoverQuestionBeadState {
  if (result?.hasGap === true) {
    return 'gap'
  }
  if (result?.answer === true) {
    return 'yes'
  }
  if (result?.answer === false) {
    return 'no'
  }
  return 'gap'
}

function readOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return null
  }
  return trimmed
}

export function buildProductDiscoverChamberState(
  workflow: LatestWorkflowForEntityResponse | undefined,
  packs?: ApplicabilityPackSummary[]
): DiscoverChamberState {
  const laws = emptyLaws(packs)
  const lawsByKey = new Map(laws.map((entry) => [entry.law, entry]))
  let followUpMessageKey: string | null = null
  let errorMessage: string | null = null
  let activeNarration: DiscoverChamberQuestion | null = null
  let lastRetrievalQuery: string | null = null

  if (workflow != null && workflow.status !== 'none') {
    for (const event of workflow.events) {
      if (event.type === 'step.started') {
        const questionStep = parseQuestionStep(event.step)
        if (questionStep != null) {
          const lawState = lawsByKey.get(questionStep.law)
          const question = lawState?.questions.find(
            (entry) => entry.questionKey === questionStep.questionKey
          )
          if (question != null) {
            question.state = 'running'
            activeNarration = question
          }
          continue
        }

        const law = lawFromFirstPassStep(event.step)
        if (law != null) {
          const lawState = lawsByKey.get(law)
          if (lawState != null) {
            lawState.phase = 'running'
          }
          continue
        }

        if (event.step === 'sync_compliance_files') {
          followUpMessageKey =
            'v2.pages.products.detail.discover.log.syncComplianceFiles'
        } else if (event.step === 'provision_machinery_compliance') {
          followUpMessageKey =
            'v2.pages.products.detail.discover.log.provisionMachineryCompliance'
        }
        continue
      }

      if (event.type === 'step.completed') {
        const questionStep = parseQuestionStep(event.step)
        if (questionStep != null) {
          const lawState = lawsByKey.get(questionStep.law)
          const question = lawState?.questions.find(
            (entry) => entry.questionKey === questionStep.questionKey
          )
          if (question != null) {
            const result =
              event.result != null && typeof event.result === 'object'
                ? (event.result as Record<string, unknown>)
                : undefined
            question.state = beadStateFromResult(result)
            question.rationale = readOptionalString(result?.rationale)
            question.retrievalQuery = readOptionalString(result?.retrievalQuery)
            if (question.retrievalQuery != null) {
              lastRetrievalQuery = question.retrievalQuery
            }
            if (activeNarration?.questionKey === question.questionKey) {
              activeNarration = null
            }
          }
        }
      }

      if (event.type === 'workflow.failed') {
        errorMessage =
          typeof event.error === 'string' && event.error.trim().length > 0
            ? event.error.trim()
            : null
      }
    }
  }

  const verdicts = extractProductDiscoverVerdicts(workflow)
  for (const law of DISCOVER_CHAMBER_LAW_ORDER) {
    const lawState = lawsByKey.get(law)
    const verdict = verdicts[law]
    if (lawState == null || verdict == null) {
      continue
    }
    lawState.skipped = verdict.skipped
    lawState.verdict = verdict.status
    lawState.phase = 'done'
  }

  const runningQuestion = laws
    .flatMap((law) => law.questions)
    .find((question) => question.state === 'running')
  if (runningQuestion != null) {
    activeNarration = runningQuestion
  }

  const countedQuestions = laws.flatMap((lawState) => {
    if (lawState.skipped) {
      return []
    }
    return lawState.questions
  })
  const answeredCount = countedQuestions.filter(
    (question) => question.state !== 'pending' && question.state !== 'running'
  ).length

  return {
    laws,
    followUpMessageKey,
    isComplete: workflow?.status === 'completed',
    isFailed: workflow?.status === 'failed',
    errorMessage,
    answeredCount,
    totalCount: countedQuestions.length,
    activeNarration,
    lastRetrievalQuery,
  }
}
